import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { sanitizeText, sanitizeLongText } from "@/lib/sanitize";
import { getCurrentUser } from "@/lib/authUser";

interface Product {
  id: string;
  name: string;
  description: string | null;
  user_id: string;
  group_id: string | null;
}

interface AddProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProductSaved: () => void;
  editProduct?: Product | null;
}

const AddProductDialog = ({ open, onOpenChange, onProductSaved, editProduct }: AddProductDialogProps) => {
  const { workspace, selectedGroupId } = useWorkspace();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editProduct) {
      setName(editProduct.name);
      setDescription(editProduct.description || "");
    } else {
      setName("");
      setDescription("");
    }
  }, [editProduct, open]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Product name is required");
      return;
    }

    setSaving(true);
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error("Not authenticated");

      const sanitizedName = sanitizeText(name, 200);
      const sanitizedDesc = sanitizeLongText(description, 2000) || null;

      if (editProduct) {
        const { error } = await supabase
          .from("products")
          .update({ name: sanitizedName, description: sanitizedDesc })
          .eq("id", editProduct.id);
        if (error) throw error;
        toast.success("Product updated");
      } else {
        // Product cap removed — every tier is unlimited under the new model.
        const { error } = await supabase.from("products").insert({
          name: sanitizedName,
          description: sanitizedDesc,
          user_id: user.id,
          group_id: workspace === "groups" && selectedGroupId ? selectedGroupId : null,
        });
        if (error) throw error;
        toast.success("Product created");
      }

      onProductSaved();
      onOpenChange(false);
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{editProduct ? "Edit Product" : "Add Product"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="product-name">Name</Label>
            <Input
              id="product-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Enterprise Plan"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="product-description">Description</Label>
            <Textarea
              id="product-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this product..."
              rows={4}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : editProduct ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddProductDialog;
